'use client'

import * as React from 'react'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'

import { cn } from '../lib/utils'
import { useAnySheetOpen } from '../lib/sheet-state'
import { Button } from './button'
import { XIcon } from 'lucide-react'

function Dialog({ onOpenChange, ...props }: DialogPrimitive.Root.Props) {
  // Suppress dialog auto-close on outside-press while ANY sheet
  // is open. Without this, clicks inside a sheet stacked over
  // the dialog (e.g. the pre-order editor opened from the
  // booking quick-dialog) get reported by base-ui as
  // "outside the dialog popup" → the dialog dismisses
  // underneath the still-open sheet. Esc / Close button still
  // close as normal — only the outside-press reason is gated.
  const sheetOpen = useAnySheetOpen()
  const sheetOpenRef = React.useRef(sheetOpen)
  sheetOpenRef.current = sheetOpen
  const handleOpenChange = React.useCallback<
    NonNullable<DialogPrimitive.Root.Props['onOpenChange']>
  >(
    (open, details) => {
      if (
        !open &&
        sheetOpenRef.current &&
        details?.reason === 'outside-press'
      ) {
        return
      }
      onOpenChange?.(open, details)
    },
    [onOpenChange]
  )
  return (
    <DialogPrimitive.Root
      data-slot="dialog"
      onOpenChange={handleOpenChange}
      {...props}
    />
  )
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      // Render the backdrop even when nested inside another modal (a Sheet /
      // Dialog). Base UI drops nested backdrops by default (`enabled:
      // forceRender || !nested`), which left a Dialog opened from within a
      // Sheet floating with no dim/blur behind it. Each modal should dim what
      // sits behind it; our backdrop is only a subtle 10% tint so stacking two
      // never reads as "too dark".
      forceRender
      // Backdrop fade. Same timing as the popup so backdrop +
      // content vanish together — mismatched durations leave
      // the popup floating un-anchored. Arbitrary `[Nms]` values
      // because the project's Tailwind scale doesn't define
      // these durations natively.
      className={cn(
        'fixed inset-0 isolate z-50 bg-black/10 transition-opacity duration-[180ms] ease-out data-starting-style:opacity-0 data-ending-style:opacity-0 data-ending-style:duration-[120ms] supports-backdrop-filter:backdrop-blur-xs',
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  keepMounted = false,
  initialFocus,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
  /**
   * Keep the dialog content in the DOM after it closes. Default
   * is `false` (base-ui's standard behaviour: tear down on close,
   * remount on next open). Set `true` for swap-style dialog
   * patterns where the same content is reopened repeatedly and
   * remounting is expensive (e.g. a templates picker whose
   * children are iframes that re-fetch network resources on
   * every mount). The popup is hidden via base-ui's open state +
   * exit animation either way.
   */
  keepMounted?: boolean
  /**
   * Element to focus when the dialog opens. Defaults to the dialog container
   * itself (not the first field) so opening a dialog never drops a cursor into
   * an input or pops the mobile keyboard — focus still enters the modal, so the
   * focus trap + Esc still work. Pass a ref to focus a specific field, or
   * `false` to leave focus on the trigger.
   */
  initialFocus?: DialogPrimitive.Popup.Props['initialFocus']
}) {
  // Default initial focus → the popup container, so no input auto-focuses on
  // open (see `initialFocus` prop). `??` keeps an explicit `false`.
  const contentRef = React.useRef<HTMLDivElement>(null)
  return (
    <DialogPortal keepMounted={keepMounted}>
      <DialogOverlay />
      <DialogPrimitive.Popup
        ref={contentRef}
        initialFocus={initialFocus ?? contentRef}
        data-slot="dialog-content"
        // See sheet.tsx for full rationale on the inline-style colour
        // workaround. Connect's Tailwind v4 + v3-config setup wraps
        // tokens in `hsl()` which collapses to transparent against the
        // full-colour `.new-ui-theme` tokens. Inline `var()` references
        // sidestep that wrapper.
        style={{
          backgroundColor: 'var(--background)',
          color: 'var(--foreground)',
        }}
        className={cn(
          // Enter: 180ms ease-out fade in.
          // Exit:  120ms ease-out fade out.
          //
          // Pure opacity, no transform. The scale-down from the
          // last attempt looked janky on content with iframes
          // because subpixel scaling caused image flicker mid-
          // transition. Plain opacity at a SHORT duration is the
          // most reliable way to avoid the "ghost imprint" — at
          // ~120ms the eye barely has time to register the
          // intermediate states. Arbitrary `[Nms]` because the
          // project's Tailwind scale doesn't define these
          // durations natively.
          'new-ui-theme fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-2xl p-4 text-sm ring-1 ring-foreground/10 outline-none transition-opacity duration-[180ms] ease-out data-starting-style:opacity-0 data-ending-style:opacity-0 data-ending-style:duration-[120ms] sm:max-w-sm',
          className
        )}
        /*
         * Lets the title reserve room for the close button. It is
         * positioned absolutely, so it takes no space in the grid and a
         * long title happily runs underneath it.
         */
        data-has-close={showCloseButton ? '' : undefined}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={
              <Button
                variant="ghost"
                className="absolute top-2 right-2"
                size="icon-sm"
              />
            }
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        '-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-2xl border-t bg-muted/50 p-4 sm:flex-row sm:justify-end',
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        'font-heading text-base leading-tight font-medium',
        // Keep clear of the close button: top-2 right-2 and size-7, so
        // 2.25rem of the top right corner is spoken for. Only applied in
        // a dialog that shows one, so titles elsewhere stay full width.
        '[[data-has-close]_&]:pr-9',
        className
      )}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
